import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Vendor {
  id: number
  display_name: string
}

interface Document {
  id: number
  display_name: string
  title: string | null
  document_date: string | null
  notes: string | null
  filename: string | null
  file_type: string | null
  file_url: string | null
  created_by: string | null
  created_at: string
}

interface Props {
  vendor: Vendor
  document: Document
  can_edit: boolean
}

export default function Show({ vendor, document: doc, can_edit }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Subcontractors", href: "/company/vendors" },
    { title: vendor.display_name, href: `/company/vendors/${vendor.id}` },
    { title: "Documents", href: `/company/vendors/${vendor.id}/documents` },
    {
      title: doc.display_name,
      href: `/company/vendors/${vendor.id}/documents/${doc.id}`,
    },
  ]

  const handleDelete = () => {
    if (confirm("Are you sure you want to remove this document?")) {
      router.delete(`/company/vendors/${vendor.id}/documents/${doc.id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${doc.display_name} - Document`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{doc.display_name}</h1>
          {can_edit && (
            <div className="space-x-2">
              <Link
                href={`/company/vendors/${vendor.id}/documents/${doc.id}/edit`}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
              >
                Edit
              </Link>
              <button
                onClick={handleDelete}
                className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
              >
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="mb-6">
          {doc.document_date && (
            <div className="mb-2">
              <span className="font-semibold">Date:</span>{" "}
              {formatDate(doc.document_date)}
            </div>
          )}

          {doc.file_url && doc.filename && (
            <div className="mb-2">
              <span className="font-semibold">File:</span>{" "}
              <a
                href={doc.file_url}
                className="text-hpi-active-blue hover:text-hpi-dark-blue"
                target="_blank"
                rel="noopener noreferrer"
              >
                {doc.filename}
              </a>
            </div>
          )}
        </div>

        {doc.notes && (
          <div className="mb-6">
            <span className="font-semibold">Notes:</span>
            <p className="mt-1 whitespace-pre-wrap">{doc.notes}</p>
          </div>
        )}

        <div className="text-hpi-med-gray text-sm">
          Added {formatDate(doc.created_at)}
          {doc.created_by && ` by ${doc.created_by}`}
        </div>

        <div className="mt-4">
          <Link
            href={`/company/vendors/${vendor.id}/documents`}
            className="text-hpi-active-blue hover:text-hpi-dark-blue"
          >
            &larr; Back to Documents
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
